import type { SharedMarketingProps } from '@/types/marketing'

interface RefundSection {
  title: string
  content: string
  bullets: string[]
  extra: string | null
}

interface RefundsPageProps extends SharedMarketingProps {
  last_updated: string
  contact_email: string
  contact_address: string
  sections: RefundSection[]
}

function Section({ section }: { section: RefundSection }) {
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold text-white">{section.title}</h2>
      <p className="text-sm leading-7 text-white/72">{section.content}</p>
      {section.bullets.length > 0 && (
        <ul className="list-disc space-y-1.5 pl-6 text-sm leading-7 text-white/72">
          {section.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      )}
      {section.extra && (
        <p className="text-sm leading-7 text-white/72">{section.extra}</p>
      )}
    </section>
  )
}

export default function Refunds(props: RefundsPageProps) {
  return (
    <div className="container mx-auto px-6 pb-20 pt-32 lg:pt-40">
      <div className="mx-auto max-w-3xl">
        <header className="mb-12">
          <h1 className="marketing-display text-3xl lg:text-4xl">Cancellation &amp; Refund Policy</h1>
          <p className="mt-4 text-sm text-white/50">
            Last updated: {props.last_updated}
          </p>
          <p className="mt-6 text-sm leading-7 text-white/72">
            This policy outlines the conditions under which payments to wavbee may or may not be
            refundable, including subscription cancellations, application fees, and service denials.
          </p>
        </header>

        <div className="space-y-10">
          {props.sections.map((section) => (
            <Section key={section.title} section={section} />
          ))}
        </div>

        <footer className="mt-14 rounded-lg border border-white/10 bg-white/[0.03] p-6">
          <h2 className="text-lg font-semibold text-white">Contact Us</h2>
          <p className="mt-2 text-sm leading-7 text-white/72">
            For refund requests or questions about this policy, contact us at{' '}
            <a href={`mailto:${props.contact_email}`} className="text-[var(--wavbee-orange)] hover:underline">
              {props.contact_email}
            </a>
          </p>
          <p className="mt-1 text-sm text-white/50">{props.contact_address}</p>
        </footer>
      </div>
    </div>
  )
}
